import React from 'react';
import { Button } from '@fiverr-private/button';
import { Center, Stack } from '@fiverr-private/layout_components';
import { List, ListItem, ListItemIcon } from '@fiverr-private/list';
import { Text } from '@fiverr-private/typography';
import { assignInlineVars, Box } from '@fiverr-private/theme';
import { CheckIcon, ExternalLinkIcon } from '@fiverr-private/visuals';
import { Image, Video } from '@fiverr-private/media';
import { ElementType } from '@fiverr-private/bianka_js';
import { getContext } from '@fiverr-private/fiverr_context';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { AppI18n } from '../../i18n';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { replaceShopifyDomain } from '../../utils/replaceShopifyDomain';
import { useStoreData } from '../../hooks/useStoreData';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { StepTitle } from '../../components/step-title/StepTitle';
import { listItemIndex, listItemStyling, imageTransitionContainer, mainContentStyling } from './CreateStore.ve.css';
import { GIF_URL, IMAGE_URL, SHOPIFY_URL } from './CreateStore.constants';

const LIST_ITEMS = [
    {
        id: 1,
        text: <AppI18n k="steps.create_store.list.item_1" />,
    },
    {
        id: 2,
        text: <AppI18n k="steps.create_store.list.item_2" />,
    },
    {
        id: 3,
        text: <AppI18n k="steps.create_store.list.item_3" />,
    },
];

export const CreateStore: React.FC = () => {
    const { pageCtxId, userId } = getContext();
    const { currentPath } = useWizardNavigation();
    const { storeData } = useStoreData();
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();
    const chosenBannerImageUrl = storeData?.banners?.[0]?.url || IMAGE_URL;

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    const handlePreviewStoreClick = () => {
        reportClickEvent({
            biEventData: {
                userId,
                ctxId: pageCtxId,
            },
            eventType: 'clicked_on_preview_store',
            elementType: ElementType.LINK,
            currentPath,
        });

        if (!storeData?.storeUrl) return;
        const shopifyUrl = replaceShopifyDomain(SHOPIFY_URL, storeData.storeUrl);
        window.open(shopifyUrl, '_blank');
    };

    return (
        <Center wrap="wrap">
            <Stack
                direction={{ default: 'column', md: 'row' }}
                gap={{ default: '8', md: '14' }}
                ref={pageImpressionRef}
            >
                <Stack direction="column" gap="8" color="grey_1200">
                    <StepTitle title={<AppI18n k="steps.create_store.title" />} />
                    <List paddingLeft="0" color="grey_1000" width={260}>
                        {LIST_ITEMS.map(({ id, text }, index) => (
                            <ListItem
                                key={id}
                                marginBottom="2"
                                fontWeight="semibold"
                                fontSize="b_sm"
                                justifyContent="spaceBetween"
                                className={listItemStyling}
                                style={{
                                    ...assignInlineVars({
                                        [listItemIndex]: `${index}`,
                                    }),
                                }}
                            >
                                {text}
                                <ListItemIcon marginTop="px">
                                    <CheckIcon size="sm" color="grey_1200" />
                                </ListItemIcon>
                            </ListItem>
                        ))}
                    </List>
                    <Stack direction="column" gap="2">
                        <Text fontWeight="semibold" className={mainContentStyling} wordBreak="keepAll">
                            <AppI18n k="steps.create_store.subtitle" />
                        </Text>
                        <Text width={{ default: 311, md: 480 }} className={mainContentStyling} wordBreak="keepAll">
                            <AppI18n
                                k="steps.create_store.description"
                                templates={{ strong: (text) => <strong>{text}</strong> }}
                            />
                        </Text>
                    </Stack>
                    <Button
                        onClick={handlePreviewStoreClick}
                        alignSelf="flexStart"
                        variant="outline"
                        className={mainContentStyling}
                    >
                        <AppI18n k="steps.create_store.cta" />
                        <ExternalLinkIcon />
                    </Button>
                </Stack>
                <Stack direction="column" gap="2" color="grey_1200" width="100%" height="100%" position="relative">
                    <Video
                        sources={[
                            {
                                type: 'video/mp4',
                                src: GIF_URL,
                            },
                        ]}
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls={false}
                        width={{ default: 310, sm: 488 }}
                        borderRadius="lg"
                    />
                    <Box className={imageTransitionContainer} position="absolute">
                        <Image
                            src={chosenBannerImageUrl}
                            width={{ default: 253, sm: 400 }}
                            height={{ default: 93, sm: 148 }}
                            borderRadius="lg"
                            alt="store preview"
                        />
                    </Box>
                </Stack>
            </Stack>
        </Center>
    );
};

CreateStore.displayName = 'CreateStore';
